'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import Link from '@/components/ui/Link';
import BreadcrumbTrail from './BreadcrumbTrail';
import AuthorTrail from './AuthorTrail';
import DynamicSocialShare from './DynamicSocialShare';
import GumletEmbed from '@/components/component_unique/GumletEmbed';
import PhotoCarousel from '@/components/component_unique/PhotoCarousel';
import HeroAdOverlay from './HeroAdOverlay';
import { MessageIconLogo } from '@/components/ui/message-icon-logo';
import { getImageUrl } from '@/lib/api';
import { buildHeroVariantUrl, buildHeroSrcSet, HERO_DEFAULT_WIDTH, TEMPLATE_HERO_SIZES } from '@/lib/heroLcp';
import { HeroField } from '@/lib/types/articleTemplate';
import { resolveSlotValue, resolveHeroMedia, resolveDateMeta } from '@/lib/articleTemplateFields';
export interface ArticleHeroSectionProps {
  heroFields: HeroField[];
  story: any;
  /** template.templateType — changes how the heroImage field renders when
   *  the Builder's own Media Type config doesn't match the template's real
   *  content: 'video' resolves videoUrl from the story itself (see
   *  resolveHeroMedia), 'photo' renders the full PhotoCarousel (all story
   *  images, not just the single hero-image-s3-key) in the field's position. */
  templateType?: string;
}
function slot(field: HeroField, key: string) {
  return field.slots?.find(s => s.key === key);
}
function TitleField(t0) {
  const $ = _c(16);
  const {
    field,
    story
  } = t0;
  let t1;
  let t2;
  if ($[0] !== field || $[1] !== story) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const titleSlot = slot(field, "title");
      if (titleSlot?.hidden) {
        t2 = null;
        break bb0;
      }
      t1 = titleSlot ? resolveSlotValue(story, titleSlot) : story?.headline;
    }
    $[0] = field;
    $[1] = story;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const value = t1;
  if (!value) {
    return null;
  }
  const t3 = field.textStyle?.align;
  const t4 = field.textStyle?.fontSize;
  const t5 = field.textStyle?.color;
  let t6;
  if ($[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = {
      textAlign: t3,
      fontSize: t4,
      color: t5
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const style = t6;
  const isLive = story?.["is-live-blog"] && !story?.metadata?.["is-closed"];
  let t7;
  if ($[8] !== isLive) {
    t7 = isLive && <span className="live-dot relative inline-flex rounded-full h-5 w-5 bg-red-600 flex-shrink-0 mt-1.5" />;
    $[8] = isLive;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== style || $[11] !== value) {
    t8 = <h1 className="text-24-tamil-700 text-1B1B1B" style={style}>{value}</h1>;
    $[10] = style;
    $[11] = value;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <div className="flex items-start gap-3 p-3">{t7}{t8}</div>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function DescriptionField(t0) {
  const $ = _c(11);
  const {
    field,
    story
  } = t0;
  let t1;
  let t2;
  if ($[0] !== field || $[1] !== story) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const descSlot = slot(field, "description");
      if (descSlot?.hidden) {
        t2 = null;
        break bb0;
      }
      t1 = descSlot ? resolveSlotValue(story, descSlot) : story?.subheadline || story?.summary;
    }
    $[0] = field;
    $[1] = story;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const value = t1;
  if (!value) {
    return null;
  }
  const t3 = field.textStyle?.align;
  const t4 = field.textStyle?.fontSize;
  const t5 = field.textStyle?.color;
  let t6;
  if ($[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = {
      textAlign: t3,
      fontSize: t4,
      color: t5
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const style = t6;
  let t7;
  if ($[8] !== style || $[9] !== value) {
    t7 = <p className="px-3 pb-3 text-16-tamil-500 text-4D4D4D" style={style}>{value}</p>;
    $[8] = style;
    $[9] = value;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function CategoryBadgeField(t0) {
  const $ = _c(24);
  const {
    field,
    story
  } = t0;
  let colorSlot;
  let label;
  let t1;
  let t2;
  if ($[0] !== field || $[1] !== story) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const labelSlot = slot(field, "sectionLabel");
      const urlSlot = slot(field, "sectionUrl");
      let t3;
      if ($[6] !== field) {
        t3 = slot(field, "sectionColor");
        $[6] = field;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      colorSlot = t3;
      if (labelSlot?.hidden) {
        t2 = null;
        break bb0;
      }
      label = labelSlot ? resolveSlotValue(story, labelSlot) : story?.sections?.[0]?.name || "";
      t1 = urlSlot ? resolveSlotValue(story, urlSlot) : "#";
    }
    $[0] = field;
    $[1] = story;
    $[2] = colorSlot;
    $[3] = label;
    $[4] = t1;
    $[5] = t2;
  } else {
    colorSlot = $[2];
    label = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const url = t1;
  const color = colorSlot?.static || "#1d4ed8";
  const title = story?.headline || "";
  let t3;
  if ($[8] !== color || $[9] !== label || $[10] !== url) {
    t3 = label ? <Link href={url || "#"} className="text-14-tamil-700" style={{
      color
    }}>{label}</Link> : <div />;
    $[8] = color;
    $[9] = label;
    $[10] = url;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = `article-hero-${story?.slug || ""}`;
  const t5 = story?.slug;
  let t6;
  if ($[12] !== story) {
    t6 = story?.["hero-image-s3-key"] ? getImageUrl(story["hero-image-s3-key"]) : undefined;
    $[12] = story;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = story?.subheadline;
  const t8 = story?.slug ? `/${story.slug}` : undefined;
  let t9;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7 || $[18] !== t8 || $[19] !== title) {
    t9 = <div className="flex items-center gap-1 text-4D4D4D shrink-0"><MessageIconLogo id={t4} variant="message-icon-3" slug={t5} title={title} thumbnailUrl={t6} description={t7} articleUrl={t8} /></div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = title;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t3 || $[22] !== t9) {
    t10 = <div className="px-3 pt-3 flex items-center justify-between">{t3}{t9}</div>;
    $[21] = t3;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}

// Matches src/components/ui/publishon.tsx exactly (label/value weights, gap,
// horizontal layout) so the dynamic hero's date row looks identical to the
// legacy renderer's, instead of stacking Published/Updated on separate lines.
function DateMetaField(t0) {
  const $ = _c(8);
  const {
    field,
    story
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== field || $[1] !== story) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const rows = resolveDateMeta(field, story);
      const visibleRows = rows.filter(_temp);
      if (visibleRows.length === 0) {
        t3 = null;
        break bb0;
      }
      t1 = "px-3 pb-3 flex items-center gap-6";
      t2 = visibleRows.map(_temp2);
    }
    $[0] = field;
    $[1] = story;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp2(row) {
  return <div key={row.key} className="flex items-center gap-1"><span className="text-12-tamil-300 text-1B1B1B">{row.label} :</span>{row.iso ? <time className="text-12-tamil-500 text-1B1B1B" dateTime={row.iso} title={row.title}>{row.value}</time> : <span className="text-12-tamil-500 text-1B1B1B">{row.value}</span>}</div>;
}
function _temp(r) {
  return r.value;
}
function HeroImageField(t0) {
  const $ = _c(39);
  const {
    field,
    story,
    templateType
  } = t0;
  const media = resolveHeroMedia(field, story, templateType);
  const [playing, setPlaying] = useState(false);
  if (templateType === "photo") {
    let t1;
    if ($[0] !== story?.cards) {
      t1 = story?.cards || [];
      $[0] = story?.cards;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = t1.flatMap(_temp3);
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const photos = t2;
    const t3 = story?.["hero-image-s3-key"];
    const t4 = story?.["hero-image-metadata"];
    const t5 = story?.["hero-image-caption"] || "";
    const t6 = story?.["hero-image-attribution"] || "";
    let t7;
    if ($[4] !== t3 || $[5] !== t4 || $[6] !== t5 || $[7] !== t6) {
      t7 = {
        imageS3Key: t3,
        imageMetadata: t4,
        title: t5,
        attribution: t6
      };
      $[4] = t3;
      $[5] = t4;
      $[6] = t5;
      $[7] = t6;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] !== photos || $[10] !== t7) {
      t8 = <div className="border-b border-DCDCDC"><PhotoCarousel heroImage={t7} photos={photos} maxHeight={575} containerClassName="aspect-[16/9] max-h-[400px]" /></div>;
      $[9] = photos;
      $[10] = t7;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    return t8;
  }
  if (media.mode === "photoGallery") {
    const items = media.galleryItems || [];
    if (items.length === 0) {
      return null;
    }
    return <div className="flex gap-2 overflow-x-auto p-3">{items.map(_temp4)}</div>;
  }
  if (media.mode === "video" || media.mode === "gumletVideo") {
    const embedSrc = media.mode === "gumletVideo" ? resolveGumletEmbedUrl(media.gumletVideoUrl) : media.videoUrl;
    const isYouTube = media.mode === "video" && !!media.videoUrl && /youtube\.com|youtu\.be/.test(media.videoUrl);
    if (!embedSrc && !media.embedJs && !media.videoPreviewImage) {
      return null;
    }
    if (!embedSrc && media.embedJs) {
      return <div className="p-3"><GumletEmbed embedJs={media.embedJs} />{(media.caption || media.attribution) && <p className="text-12-tamil-400 text-808080 mt-1">{media.caption && <span className="text-12-tamil-600 text-1B1B1B">{media.caption}</span>}{media.caption && media.attribution && " - "}{media.attribution}</p>}</div>;
    }
    if (!embedSrc) {
      let t1;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          paddingBottom: "56.25%"
        };
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      return <div className="p-3"><div className="relative w-full" style={t1}><img src={buildHeroVariantUrl(media.videoPreviewImage, HERO_DEFAULT_WIDTH) || media.videoPreviewImage} alt="" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" /></div>{(media.caption || media.attribution) && <p className="text-12-tamil-400 text-808080 mt-1">{media.caption && <span className="text-12-tamil-600 text-1B1B1B">{media.caption}</span>}{media.caption && media.attribution && " - "}{media.attribution}</p>}</div>;
    }
    let t1;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        paddingBottom: "56.25%"
      };
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    const t2 = !playing ? <button type="button" onClick={() => setPlaying(true)} className="absolute inset-0 w-full h-full flex items-center justify-center" aria-label="Play video">{media.videoPreviewImage && <img src={buildHeroVariantUrl(media.videoPreviewImage, HERO_DEFAULT_WIDTH) || media.videoPreviewImage} alt="" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />}<span className="relative z-10 w-16 h-16 rounded-full bg-black/60 flex items-center justify-center"><span className="w-0 h-0 ml-1" style={{
          borderTop: "10px solid transparent",
          borderBottom: "10px solid transparent",
          borderLeft: "16px solid white"
        }} /></span></button> : isYouTube ? <iframe src={embedSrc} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen={true} title="Video" /> : media.mode === "gumletVideo" ? <iframe src={embedSrc} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen={true} title="Video" /> : <video src={embedSrc} controls={true} autoPlay={true} className="absolute inset-0 w-full h-full object-cover" />;
    let t3;
    if ($[14] !== t2) {
      t3 = <div className="relative w-full bg-black" style={t1}>{t2}</div>;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    return <div className="p-3">{t3}{(media.caption || media.attribution) && <p className="text-12-tamil-400 text-808080 mt-1">{media.caption && <span className="text-12-tamil-600 text-1B1B1B">{media.caption}</span>}{media.caption && media.attribution && " - "}{media.attribution}</p>}</div>;
  }
  if (!media.imageSrc) {
    return null;
  }
  const aspectRatio = field.imageStyle?.aspectRatio && field.imageStyle.aspectRatio !== "Auto" ? field.imageStyle.aspectRatio : undefined;
  const objectFit = field.imageStyle?.objectFit || "cover";
  const ad = field.heroAd;
  const adShown = !!ad && !ad.hidden && !!(ad.desktopCode || ad.mobileCode);
  const t1 = "p-3";
  const t2 = "relative w-full overflow-hidden";
  let t3;
  if ($[16] !== aspectRatio) {
    t3 = aspectRatio ? {
      aspectRatio: aspectRatio.replace(":", "/")
    } : undefined;
    $[16] = aspectRatio;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const t4 = buildHeroVariantUrl(media.imageSrc, HERO_DEFAULT_WIDTH) || media.imageSrc;
  const t5 = buildHeroSrcSet(media.imageSrc);
  const t6 = media.imageAlt || "";
  const t7 = field.imageStyle?.borderRadius;
  let t8;
  if ($[18] !== objectFit || $[19] !== t7) {
    t8 = {
      objectFit,
      borderRadius: t7
    };
    $[18] = objectFit;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t8) {
    t9 = <img src={t4} srcSet={t5} sizes={TEMPLATE_HERO_SIZES} alt={t6} fetchPriority="high" loading="eager" decoding="async" className="w-full h-full" style={t8} />;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== ad || $[27] !== adShown) {
    t10 = adShown && <HeroAdOverlay ad={ad} />;
    $[26] = ad;
    $[27] = adShown;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t3 || $[31] !== t9) {
    t11 = <div className={t2} style={t3}>{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t3;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== media.attribution || $[34] !== media.caption) {
    t12 = (media.caption || media.attribution) && <p className="text-12-tamil-400 text-808080 mt-1">{media.caption && <span className="text-12-tamil-600 text-1B1B1B">{media.caption}</span>}{media.caption && media.attribution && " - "}{media.attribution}</p>;
    $[33] = media.attribution;
    $[34] = media.caption;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = <div className={t1}>{t11}{t12}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}

/** Resolve a Gumlet URL/ID to an embeddable https://play.gumlet.io/embed/<id> URL. */
function _temp4(item) {
  return <div key={item.id} className="flex-shrink-0 w-[220px]"><img src={buildHeroVariantUrl(item.imageSrc, 480) || item.imageSrc} alt={item.caption || ""} className="w-full h-[140px] object-cover rounded" loading="lazy" decoding="async" />{item.caption && <p className="text-12-tamil-400 text-808080 mt-1">{item.caption}</p>}</div>;
}
function _temp3(card) {
  return (card["story-elements"] || []).map(element => ({
    id: `${card.id}-${element.id}`,
    title: element.title,
    imageS3Key: element["image-s3-key"],
    text: element.text,
    imageMetadata: element["image-metadata"],
    attribution: element["image-attribution"] || ""
  }));
}
function resolveGumletEmbedUrl(input?: string): string | undefined {
  if (!input) return undefined;
  const watchOrEmbedMatch = input.match(/gumlet\.io\/(?:watch|embed)\/([a-zA-Z0-9]+)/);
  if (watchOrEmbedMatch) return `https://play.gumlet.io/embed/${watchOrEmbedMatch[1]}`;
  if (/^[a-zA-Z0-9]+$/.test(input)) return `https://play.gumlet.io/embed/${input}`;
  return input;
}

/**
 * The article hero — renders the configured HeroField[] in order, honoring
 * hidden/sameRowAsPrevious. heroImage switches on mediaMode (image/video/
 * gumletVideo/photoGallery), matching the Builder's ArticleHeroSection spec.
 */
export default function ArticleHeroSection(t0) {
  const $ = _c(5);
  const {
    heroFields,
    story,
    templateType
  } = t0;
  let t1;
  let t2;
  if ($[0] !== heroFields || $[1] !== story || $[2] !== templateType) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visibleFields = (heroFields || []).filter(_temp5).sort(_temp6);
      if (visibleFields.length === 0) {
        t2 = null;
        break bb0;
      }
      const shareUrl = story?.slug ? `https://www.dinamani.com/${story.slug}` : "";
      const shareTitle = story?.headline || "";
      const renderField = field => {
        switch (field.id) {
          case "breadcrumb":
            {
              return <BreadcrumbTrail items={field.breadcrumbItems} story={story} />;
            }
          case "categoryBadge":
            {
              return <CategoryBadgeField field={field} story={story} />;
            }
          case "title":
            {
              return <TitleField field={field} story={story} />;
            }
          case "description":
            {
              return <DescriptionField field={field} story={story} />;
            }
          case "heroImage":
            {
              return <HeroImageField field={field} story={story} templateType={templateType} />;
            }
          case "dateMeta":
            {
              return <DateMetaField field={field} story={story} />;
            }
          case "socialShare":
            {
              const shareUrlSlot = slot(field, "shareUrl");
              const shareTitleSlot = slot(field, "shareTitle");
              return <DynamicSocialShare items={field.items} shareUrl={shareUrlSlot ? resolveSlotValue(story, shareUrlSlot) || shareUrl : shareUrl} shareTitle={shareTitleSlot ? resolveSlotValue(story, shareTitleSlot) || shareTitle : shareTitle} iconShape={field.socialIconShape} />;
            }
          case "authorBlock":
            {
              return <AuthorTrail field={field} story={story} />;
            }
          default:
            {
              return null;
            }
        }
      };
      const rows = [];
      for (const field_0 of visibleFields) {
        if (field_0.sameRowAsPrevious && rows.length > 0) {
          rows[rows.length - 1].push(field_0);
        } else {
          rows.push([field_0]);
        }
      }
      t1 = <div className="border-b border-DCDCDC">{rows.map((rowFields, idx) => rowFields.length > 1 ? <div key={idx} className="flex flex-wrap items-center justify-between">{rowFields.map(field_1 => <div key={field_1.id}>{renderField(field_1)}</div>)}</div> : <div key={idx}>{renderField(rowFields[0])}</div>)}</div>;
    }
    $[0] = heroFields;
    $[1] = story;
    $[2] = templateType;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
}
function _temp6(a, b) {
  return a.order - b.order;
}
function _temp5(f) {
  return !f.hidden;
}